import { useRef, useState, useEffect, RefObject } from 'react';

interface UseDragToScrollReturn {
    scrollRef: RefObject<HTMLDivElement | null>;
    isDragging: boolean;
    handleMouseDown: (e: React.MouseEvent) => void;
    handleMouseUp: () => void;
    handleMouseMove: (e: React.MouseEvent) => void;
    handleMouseLeave: () => void;
}

interface UseDragToScrollOptions {
    scrollSpeed?: number;
    friction?: number;
    momentumThreshold?: number;
}

export const useDragToScroll = (
    options: UseDragToScrollOptions = {}
): UseDragToScrollReturn => {
    const {
        scrollSpeed = 1.5,
        friction = 0.97,
        momentumThreshold = 0.5
    } = options;

    const scrollRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const velocityRef = useRef(0);
    const lastXRef = useRef(0);
    const lastTimeRef = useRef(0);
    const animationRef = useRef<number | null>(null);

    // Очистка анимации при размонтировании
    useEffect(() => {
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    const applyMomentum = () => {
        if (!scrollRef.current) return;

        if (Math.abs(velocityRef.current) > momentumThreshold) {
            scrollRef.current.scrollLeft -= velocityRef.current;
            velocityRef.current *= friction;
            animationRef.current = requestAnimationFrame(applyMomentum);
        } else {
            velocityRef.current = 0;
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!scrollRef.current) return;

        // Предотвращаем перетаскивание изображений и выделение текста
        e.preventDefault();

        // Останавливаем инерцию, если она активна
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
            animationRef.current = null;
        }

        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
        scrollRef.current.style.cursor = 'grabbing';
        scrollRef.current.classList.add('dragging');

        velocityRef.current = 0;
        lastXRef.current = e.pageX;
        lastTimeRef.current = Date.now();
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        if (scrollRef.current) {
            scrollRef.current.style.cursor = 'grab';
            scrollRef.current.classList.remove('dragging');
        }

        // Инерционный скролл (не получается)
        // applyMomentum();
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging || !scrollRef.current) return;
        e.preventDefault();

        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * scrollSpeed;
        scrollRef.current.scrollLeft = scrollLeft - walk;

        // Вычисляем скорость для инерции
        const currentTime = Date.now();
        const timeDelta = currentTime - lastTimeRef.current;

        if (timeDelta > 0) {
            velocityRef.current = (e.pageX - lastXRef.current) / timeDelta * 16; // Нормализуем к 60fps
            lastXRef.current = e.pageX;
            lastTimeRef.current = currentTime;
        }
    };

    const handleMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);
            if (scrollRef.current) {
                scrollRef.current.style.cursor = 'grab';
                scrollRef.current.classList.remove('dragging');
            }

            // Инерционный скролл отключен
            // applyMomentum();
        }
    };

    return {
        scrollRef,
        isDragging,
        handleMouseDown,
        handleMouseUp,
        handleMouseMove,
        handleMouseLeave,
    };
};

